import React, { useState, useEffect } from "react";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { isAfter, parse, isValid, format } from "date-fns";

import {
  Flex,
  SimpleGrid,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  VStack,
} from "@chakra-ui/react";

import {
  Permission,
  referenciaFunc,
} from "../../../../../../../helpers/Funcionalidades";
import { CustomButton } from "../../../../../../components/CustomButton";
import CustomDropzone from "../../../../../../components/CustomDropzone";
import { CustomSelectWField } from "../../../../../../components/CustomSelectWField";
import { FocusError } from "../../../../../../components/FocusError";
import { CustomDatepickerWField } from "../../../../../../components/CustomDatepickerWField";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import RestricaoService from "../../../../../../../main/services/RestricaoService";
import { useAppContext } from "../../../../../../../Context/MessageContextProvider";
import { hasFormChanged } from "../../../../../../utils/hasFormChanged";
import { useHistory, useLocation } from "react-router-dom";
import { deleteUrlParam } from "../../../../../../utils/deleteUrlParam";

const suportedFormats = [
  ".png",
  ".bmp",
  ".tiff",
  ".jpg",
  ".jpeg",
  ".doc",
  ".docx",
  ".txt",
  ".dot",
  ".pdf",
  ".xml",
  ".xls",
  ".xlam",
  ".ods",
];

const schema = Yup.object().shape({
  pessoa: Yup.object().nullable().required("Este campo é obrigatório!"),
  dataProibicao: Yup.string()
    .required("Este campo é obrigatório!")
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!!value) {
        if (value.length === 10) {
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        } else {
          return false;
        }
      } else {
        return true;
      }
    })
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      }
    ),
  anexoGuia: Yup.mixed().test(
    "fileType",
    "Anexar uma extensão válida.",
    (value) => {
      if (value === null) {
        return true;
      } else {
        let haveFormat = false;
        suportedFormats.forEach((format) => {
          if (value.path.toString().toLowerCase().includes(format)) {
            haveFormat = true;
          }
        });
        return haveFormat;
      }
    }
  ),
});

export const RestricoesModal = ({
  isOpen,
  onOpen,
  onClose,
  onCloseFiliacoes,
  form,
  filiacoes,
  setNovaFiliacao,
  initialFiliacao,
  roleKeycloack,
  idCrianca,
  fetchDataRestricoes,
  relations,
  handleGetRelacionamento,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [initialValues] = useState({
    pessoa: "",
    dataProibicao: "",
    anexoGuia: null,
  });
  const { cadastrarRestricao, setCadastrarRestricao } = useAppContext();
  const searchParams = new URLSearchParams(location.search);
  const idTipoPendencia = searchParams.get("idTipoPendencia");
  const idAnexo = searchParams.get("idAnexo");

  const backAlert = () => {
    if (cadastrarRestricao) {
      Swal.fire({
        title: "Tem certeza que deseja cancelar?",
        text: "As informações serão perdidas.",
        icon: "error",
        showCancelButton: true,
        confirmButtonColor: "#169499",
        cancelButtonColor: "#169499",
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
      }).then((result) => {
        if (result.isConfirmed) {
          onClose();
          setCadastrarRestricao(false);
        }
      });
    } else {
      onClose();
    }
  };

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  const adicionarRestricao = async (id, valuesToSend) => {
    try {
      await RestricaoService.postRestricao(id, valuesToSend);

      deleteUrlParam("idAnexo", history);

      (["8", "9"].includes(idAnexo) ||
        ["44", "45"].includes(idTipoPendencia)) &&
        history.replace(location.pathname);
      Swal.fire({
        title: "Sucesso!",
        text: "Registro cadastrado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {});
      onClose();
      fetchDataRestricoes();
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: "Atenção!",
        text: error.response.data.message ? error.response.data.message : "",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      });
    }
  };

  const handleSubmit = (values, actions) => {
    setTimeout(() => {
      const { pessoa, anexoGuia, dataProibicao } = values;

      const splitDate = dataProibicao.split("/");
      const year = parseInt(splitDate[2]);
      const month = parseInt(splitDate[1]) - 1;
      const day = parseInt(splitDate[0]);
      const now = new Date();

      const formattedDate = new Date(
        year,
        month,
        day,
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );
      const formattedDateString = format(
        formattedDate,
        "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
      );
      const anexoRequest = 0;

      if (anexoGuia) {
        RestricaoService.postAnexoTermoRestricao(
          idCrianca,
          anexoGuia,
          anexoRequest
        ).then((res) => {
          const { idAnexo, idCrianca } = res.data;

          const valuesToSend = {
            idAnexoRestricao: idAnexo ? idAnexo : 0,
            idPessoaFisicaRestricao: pessoa ? pessoa.id : "",
            dataProibicao: formattedDateString,
          };

          if (idAnexo && idCrianca) {
            adicionarRestricao(idCrianca, valuesToSend);
          }
        });
      } else {
        const valuesToSend = {
          idAnexoRestricao: 0,
          idPessoaFisicaRestricao: pessoa ? pessoa.id : "",
          dataProibicao: formattedDateString,
        };
        adicionarRestricao(idCrianca, valuesToSend);
      }

      actions.setSubmitting(false);
    }, 200);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent
          p={{ base: "5px", sm: "15px", md: "25px" }}
          w={{ base: "100%", lg: "40%" }}
          bgColor="#FFF"
        >
          <ModalHeader
            p="0px"
            fontWeight={700}
            fontSize="14px"
            lineHeight="19.6px"
            color="#169499"
          >
            {"Proibição".toUpperCase()}
          </ModalHeader>
          <ModalBody
            p="0px"
            mt="25px"
            minH={{ base: "0px", md: "200px" }}
            pl="1px"
          >
            <Formik
              validationSchema={schema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {(props) => (
                <Form style={{ width: "100%" }}>
                  {setCadastrarRestricao(
                    hasFormChanged(initialValues, props.values)
                  )}
                  <FocusError />
                  <Grid templateColumns="repeat(3, 1fr)" gap="15px" w="100%">
                    <GridItem colSpan={{ base: 6 }}>
                      <CustomSelectWField
                        fieldName="pessoa"
                        label="Pessoa com restrição a visitação*"
                        placeholder="Selecione a pessoa com restrição"
                        getOptionLabel={(option) =>
                          option.nome +
                          `${
                            option.nomeSocial ? ` / ${option.nomeSocial}` : ""
                          }`
                        }
                        getOptionValue={(option) => option.id}
                        isLoading={relations.isLoading}
                        options={relations.options}
                        onChange={(value) => {
                          props.setFieldValue("pessoa", value);
                        }}
                        onFocus={handleGetRelacionamento}
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 4, lg: 2 }}>
                      <VStack alignItems="flex-start">
                        <CustomDatepickerWField
                          fieldName="dataProibicao"
                          label="Data da Proibição*"
                          placeholder="DD/MM/AAAA"
                          maxDate={new Date()}
                        />
                      </VStack>
                    </GridItem>
                    <GridItem colSpan={{ base: 6, lg: 4 }} />
                    <Permission
                      roleKeycloack={roleKeycloack}
                      func={referenciaFunc.guardaAnexar}
                    >
                      <GridItem colSpan={{ base: 6, lg: 1 }}>
                        <Flex>
                          <CustomDropzone
                            formProps={props}
                            title={`Anexar Decisão Judicial`}
                            fieldName="anexoGuia"
                            height="42px"
                            titleText="Anexar Decisão Judicial"
                          />
                        </Flex>
                      </GridItem>
                    </Permission>
                    <GridItem colSpan={{ base: 6, lg: 5 }}>
                      <Flex justifyContent={"end"}>
                        <SimpleGrid
                          mt="25px"
                          columns={2}
                          spacing="15px"
                          w={{ base: "100%", lg: "80%" }}
                        >
                          <CustomButton onClick={backAlert} label="Cancelar" />
                          <CustomButton
                            type="submit"
                            translate="no"
                            isLoading={props.isSubmitting}
                            label="Salvar"
                          />
                        </SimpleGrid>
                      </Flex>
                    </GridItem>
                  </Grid>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
